import { useEffect, useRef, useState } from "react";
import { Chip, Menu, MenuItem, Popover } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { DUE_OPTIONS, formatDueLabel, addDays, isOverdue } from "../../utils/taskUtils";
import { getFilledChipStyles, getEmptyChipStyles, getDueDateChipStyles } from "./taskChipStyles";
import { DateYMDString } from "@/types";
import { ChipSize, UIComponentSize } from "@/types/shared/uiComponents";

interface DueDateSelectorProps {
  /** Currently selected due date (YYYY-MM-DD format) */
  value: DateYMDString | null;
  /** Callback when selection changes */
  onChange: (date: DateYMDString | null) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Chip size variant */
  size?: ChipSize;
  /** Disable past dates in the date picker */
  disablePast?: boolean;
  /** Show overdue styling when date is past */
  showOverdueStyle?: boolean;
  /**
   * Controlled-open mirror. When the parent sets this to `false` any open
   * menu/popover is force-closed. Lets the composer close selectors before the
   * chip row unmounts, preventing a leaked MUI scroll-lock that froze the task
   * drawer (#5158 / #6310 AC4).
   */
  open?: boolean;
  /** Notifies the parent whenever a menu/popover opens or closes. */
  onOpenChange?: (open: boolean) => void;
}

export function DueDateSelector({
  value,
  onChange,
  disabled = false,
  size = UIComponentSize.Medium,
  disablePast = false,
  showOverdueStyle = true,
  open,
  onOpenChange,
}: DueDateSelectorProps) {
  // Single stable anchor (the chip). Both the quick-select Menu and the date
  // Popover anchor here — never to a MenuItem, which unmounts with the Menu and
  // left the Popover anchored to a detached node (root cause of #5158).
  const chipRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<"menu" | "calendar" | null>(null);

  // Parent force-close: when controlled `open` flips false, dismiss any popup so
  // it never unmounts while open (which leaks the body scroll-lock).
  useEffect(() => {
    if (open === false) setView(null);
  }, [open]);

  const setViewAndNotify = (next: "menu" | "calendar" | null) => {
    setView(next);
    onOpenChange?.(next !== null);
  };

  const handleClick = () => {
    if (!disabled) {
      setViewAndNotify("menu");
    }
  };

  const handleQuickSelect = (days: number) => {
    onChange(addDays(days));
    setViewAndNotify(null);
  };

  const handleCustomDateSelect = (date: dayjs.Dayjs | null) => {
    if (date) {
      onChange(date.format("YYYY-MM-DD") as DateYMDString);
    }
    setViewAndNotify(null);
  };

  // Swap the Menu for the calendar Popover, both anchored to the stable chip.
  const openDatePicker = () => {
    setView("calendar");
  };

  const displayLabel = value ? formatDueLabel(value) : null;
  const isDateOverdue = showOverdueStyle && isOverdue(value, false);

  // Determine chip styles
  const getChipStyles = () => {
    if (!value) {
      return getEmptyChipStyles({ size, clickable: !disabled });
    }
    if (showOverdueStyle) {
      return getDueDateChipStyles(isDateOverdue, true, { size, clickable: !disabled });
    }
    return getFilledChipStyles({ size, clickable: !disabled });
  };

  return (
    <>
      <Chip
        ref={chipRef}
        icon={<CalendarTodayOutlinedIcon />}
        label={displayLabel ?? "Due"}
        onClick={handleClick}
        size="small"
        sx={getChipStyles()}
      />

      <Menu
        anchorEl={chipRef.current}
        open={view === "menu"}
        onClose={() => setViewAndNotify(null)}
        disableScrollLock
        slotProps={{
          paper: {
            sx: { borderRadius: 2, minWidth: 140 },
          },
        }}
      >
        {DUE_OPTIONS.map((opt) => (
          <MenuItem key={opt.label} onClick={() => handleQuickSelect(opt.days)}>
            {opt.label}
          </MenuItem>
        ))}
        <MenuItem onClick={openDatePicker}>Choose date...</MenuItem>
      </Menu>

      <Popover
        open={view === "calendar"}
        anchorEl={chipRef.current}
        onClose={() => setViewAndNotify(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        disableScrollLock
        slotProps={{
          paper: {
            sx: { borderRadius: 2 },
          },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateCalendar
            value={value ? dayjs(value) : null}
            onChange={handleCustomDateSelect}
            disablePast={disablePast}
          />
        </LocalizationProvider>
      </Popover>
    </>
  );
}
